import type { BaseParamsDto, IInsuranceParams, Variant } from '.'
import type { Partner } from '../enums'
import { RiskVariant } from '../enums'
import type { StepBase } from './StepBase'

export type InsuranceParamsPropertyDto = {
    property: Property
    baseParams?: BaseParamsDto
} & StepBase & IInsuranceParams

export interface Property {
    range: number
    type?: number | null
    spol?: number | null
    liability?: number | null
    responsibilityRealestate?: number | null
    insurers: number[]
    data: InsuranceParamsPropertyDataDto
    variants?: Variant[]
}

const defaultAddress = {
    addressPoint: null,
    street: '',
    withoutStreet: false,
    streetNumber: '',
    landRegistryNumber: null,
    houseNumber: '',
    postCode: '',
    postCodeCu: '',
    city: '',
    cityWithCityPart: '',
    cityPart: '',
    isCatastralNumber: false,
    isOutsideUrbanArea: false,
    isOutsideUrbanAreaAvailable: false,
    catastralNumberList: [],
    catastralAreaCode: '',
    parcelNumber: '',
    fullAddress: '',
    isEvidenceNumber: false
}

export const InitialPropertyValues = (partner?: keyof typeof Partner): Property => ({
    range: RiskVariant.MiniRange,
    type: null,
    spol: null,
    liability: null,
    responsibilityRealestate: null,
    insurers: [],
    data: {
        realtyTypeId: null,
        isDamaged: null,
        isRoofFlammable: null,
        hasNonresidentialPremises: null,
        hasBasement: null,
        address: { ...defaultAddress },
        household: {
            livingArea: null,
            floor: null,
            isApartmentOnLastFloor: false,
            equipment: null,
            isPernamentlyInhabited: false,
            countOfDamages: null,
            amount: null,
            valuablesAmount: null,
            nonresidentialAmount: null,
            countOfPeople: null,
            apartmentNumber: '',
            approvedYear: null,
            structuralType: null,
            floorsCount: null,
            roofType: null,
            isRoofResidential: false,
            owner: null,
            amountSum: null,
        },
        realty: {
            builtUpAreaBusiness: 0,
            numberOfParkings: 1,
            isDamaged: null,
            isRoofFlammable: null,
            apartmentNumber: '',
            apartmentFloor: null,
            isApartmentOnLastFloor: null,
            floorsCount: null,
            isInFamilyHouse: null,
            isInBuiltUpArea: null,
            distanceFromBuildUpArea: null,
            wasDamagedByFlood: null,
            countOfFoold1: null,
            countOfFoold2: null,
            isApproved: null,
            approvedYear: null,
            builtUpArea: null,

            builtUpCadastralArea: null,
            otherArea: null,
            usefulArea: null,
            buildingFlammability: null,
            structuralType: null,
            owner: null,
            apparmentType: null,
            state: null,
            basementArea: null,
            structuralElement: null,
            buildingType: null,
            builtDate: '',
            wasReconstructed: null,
            reconstructionDate: '',
            finishState: null,
            roofType: null,
            isRoofResidential: null,
            atticArea: null,
            isPernamentlyInhabited: null,
            countOfDamages: null,
            realtyAmount: null,
            gardenFlora: null,
            usedBy: null,
            goodState: null,
            secondaryRealty: null,
            amountSum: null,
            newOrInConstruction: null,
            startConstructionDate: null,
            proposedEndConstructionDate: null,
            elevator: null,
            isAppartmentInAttic: null,
            interiorState: null,
            flatDisposition: null,
            annuity: null,
            isPanel: null,
            hasLoggia: null,
            loggiaArea: null,
            hasTerrace: null,
            terraceArea: null,
            hasCellar: null,
            hasGarden: null,
            gardenArea: null,
            hasParking: null,
            parkingType: null,
        },
        secondaryRealties: [], // Prázdné pole pro SecondaryRealtyDto
        hasGarageOnDifferentLocation: null,
        garageAddress: { ...defaultAddress },
        garageAtOtherLocationAmount: null,
        floodOccurence: null,
        floodOccurence20Available: null,
        floodOccurence10: null,
        floodSafeAppartment: null,
        csobBaScoring: null,
        floodInsuranceRequired: partner !== 'OkGroup',
        propojistenostDto: {
            enabled: null,
            isApplicable: null,
            contracts: []
        },
        propertyRelationId: null,
        bigRiskCompany: null,
        propertyValorisationAgreement: null,
        insuranceInNewPrice: null,
        spolVariantId: null,
        longTimeRent: null,
        newHome: null,
        newHomeAcquiryDate: null,
        concurrentInsuranceWithOtherInsurer: null,
        concurrentHouseholdInsuranceValidTo: null,
        concurrentRealtyInsuranceValidTo: null
    },
    variants: []
})

export const InitialLiabilityValues = (partner?: keyof typeof Partner) => {
    const initialPropertyValues = InitialPropertyValues(partner)
    return ({
        ...initialPropertyValues,
        range: null,
        data: {
            ...initialPropertyValues.data,
            realty: {
                ...initialPropertyValues.data.realty,
                builtUpAreaBusiness: null,
                numberOfParkings: null,
            }
        }
    })
}

export interface InsuranceParamsPropertyDataDto {
    realtyTypeId?: number | null
    isDamaged?: boolean | null
    isRoofFlammable?: boolean | null
    hasNonresidentialPremises?: boolean | null
    hasBasement?: boolean | null
    address?: Address
    household?: HouseholdDto
    realty?: RealtyDto
    secondaryRealties?: SecondaryRealtyDto[]
    hasGarageOnDifferentLocation?: boolean | null
    garageAddress?: Address
    garageAtOtherLocationAmount?: number | null
    floodOccurence?: number | null
    floodOccurence20Available?: boolean | null
    floodOccurence10?: number | null
    floodSafeAppartment?: boolean | null
    csobBaScoring?: boolean | null
    floodInsuranceRequired?: boolean | null
    propojistenostDto?: PropojistenostDto
    propertyRelationId?: number | null
    bigRiskCompany?: boolean | null
    propertyValorisationAgreement?: boolean | null
    insuranceInNewPrice?: boolean | null
    spolVariantId?: number | null
    longTimeRent?: boolean | null
    newHome?: boolean | null
    newHomeAcquiryDate?: Date | null
    concurrentInsuranceWithOtherInsurer?: boolean | null
    concurrentHouseholdInsuranceValidTo?: Date | null
    concurrentRealtyInsuranceValidTo?: Date | null
}

export interface Address {
    addressPoint?: number | null
    street: string
    withoutStreet: boolean
    streetNumber: string
    landRegistryNumber?: number | null
    houseNumber: string
    postCode: string
    postCodeCu: string
    city: string
    cityWithCityPart: string
    cityPart: string
    isCatastralNumber: boolean
    isOutsideUrbanArea: boolean
    isOutsideUrbanAreaAvailable: boolean
    catastralNumberList?: AddressKuSelection[]
    catastralAreaCode: string
    parcelNumber: string
    fullAddress: string
    isEvidenceNumber?: boolean
    parcelNumberTypeAvailable?: boolean
    parcelNumberType?: number
}

export interface AddressKuSelection {
    kod: string
    nazev: string
    selected: boolean
}

export interface HouseholdDto {
    livingArea?: number | null
    floor?: number | null
    isApartmentOnLastFloor?: boolean
    equipment?: number | null
    isPernamentlyInhabited?: boolean
    countOfDamages?: number | null
    amount?: number | null
    valuablesAmount?: number | null
    nonresidentialAmount?: number | null
    countOfPeople?: number | null
    apartmentNumber: string
    approvedYear?: number | null
    structuralType: number | null
    floorsCount?: number | null
    roofType: number | null
    isRoofResidential?: boolean
    owner: number | null
    amountSum: number | null
}

export interface RealtyDto {
    isDamaged?: boolean | null
    isRoofFlammable?: boolean | null
    apartmentNumber: string
    apartmentFloor?: number | null
    isApartmentOnLastFloor?: boolean | null
    floorsCount?: number | null
    isInFamilyHouse?: boolean | null
    isInBuiltUpArea?: boolean | null
    distanceFromBuildUpArea?: boolean | null
    wasDamagedByFlood?: boolean | null
    countOfFoold1?: number | null
    countOfFoold2?: number | null
    isApproved?: boolean | null
    approvedYear?: number | null
    builtUpArea?: number | null
    builtUpAreaBusiness?: number | null
    builtUpCadastralArea?: number | null
    otherArea?: number | null
    usefulArea?: number | null
    buildingFlammability: number | null
    structuralType: number | null
    owner: number | null
    apparmentType: number | null
    state: number | null
    basementArea: number | null
    structuralElement: number | null
    buildingType: number | null
    builtDate?: string
    wasReconstructed?: boolean | null
    reconstructionDate?: string
    finishState: number | null
    roofType: number | null
    isRoofResidential?: boolean | null
    atticArea?: number | null
    isPernamentlyInhabited?: boolean | null
    countOfDamages?: number | null
    realtyAmount?: number | null
    gardenFlora?: number | null
    usedBy?: number | null
    goodState?: boolean | null
    secondaryRealty?: boolean | null
    secondaryBuildingsArea?: number | null
    amountSum: number | null
    newOrInConstruction?: boolean | null
    startConstructionDate?: Date | null
    proposedEndConstructionDate?: Date | null
    elevator?: boolean | null
    isAppartmentInAttic?: boolean | null
    interiorState?: number | null
    flatDisposition?: number | null
    annuity?: number | null
    isPanel?: boolean | null
    hasLoggia?: boolean | null
    loggiaArea?: number | null
    hasTerrace?: boolean | null
    terraceArea?: number | null
    hasCellar?: boolean | null
    hasGarden?: boolean | null
    gardenArea?: number | null
    hasParking?: boolean | null
    parkingType?: number | null
    numberOfParkings?: number | null
}

export interface SecondaryRealtyDto {
    type?: number
    otherArea?: number
    usefulArea?: number
    secondaryRealtyAmount?: number
    description: string
    delete: boolean
}

export interface PropojistenostDto {
    enabled: boolean | null
    isApplicable: boolean | null
    contracts?: PropopjistenostSmlouvaDto[]
}

export interface PropopjistenostSmlouvaDto {
    insurerId: number
    contranctNumber: string
    insurerName: string
}

export type Step1PropertyWarning = {
    message: string
    isRecreationalProperty: boolean
    isAddressLocationCadastral: boolean
}
